import * as Sentry from '@sentry/react';
import { skipToken, useQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Logger } from '@gitbook/util-debug';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigationState } from '../../navigation';
import { apiQuery } from '../../state';
import { useCurrentOrganizationId } from '../../state/organization';
import {
    generateConversationLocalId,
    useChangeRequestConversations,
    useAgentEnabled,
    type OrganizationAgentLocation,
    useDeleteOrganizationConversation,
    provisionOrganizationConversation,
    useOrganizationConversations,
} from '../Agent';
import { UpgradeDialogProvider } from '../UpgradeDialog';
import { AgentDockBar } from './AgentDockBar';
import type { AgentDockConversationControls } from './AgentDockConversation';
import { useAgentDockSlot } from './AgentDockSlot';
import { AgentSidePanel } from './AgentSidePanel';
import { type AgentDockMode, useAgentDockLayout, useMarkAgentDockFailed } from './layout';
import { type AgentDockTab, getAgentDockSurface, getAgentDockTabId, useAgentDock } from './state';

const logger = Logger('app:agentDock');

/**
 * The GitBook Agent, docked to the bottom of the application.
 *
 * Mounted in the application frame rather than inside a screen, so it survives navigation: a
 * conversation keeps running while the user moves around the product, and never reloads. It is the
 * only agent surface — there is no floating panel and no side panel to dock into.
 */
export function AgentDock() {
    const organizationId = useCurrentOrganizationId();
    const { screen } = useNavigationState();
    const isFullScreen = screen.type === 'agent';

    // Decided immediately without an organization: the dock never renders, and nothing else would
    // report the answer that screenshot capture waits on.
    React.useEffect(() => {
        if (!organizationId) {
            markAgentDockAbsent(true);
        }
    }, [organizationId]);

    // Outside an organization (login, account settings, onboarding, …) there is no agent to talk
    // to. Checked before anything reads the organization: the AI toggle lives on it, and the hook
    // that reads it throws rather than returning null when there is none. Nothing is recorded for
    // the splash here — a login screen says nothing about the frame the user comes back to.
    if (!organizationId) {
        return null;
    }

    return <AgentDockForOrganization organizationId={organizationId} isFullScreen={isFullScreen} />;
}

/**
 * Mark the layout when there is no dock, so the row reserved for its bar collapses with it. Set
 * only once the answer is known, so the row is not taken away and given back on every load.
 */
function markAgentDockAbsent(absent: boolean) {
    document.documentElement.classList.toggle('no-agent-dock', absent);
    markAgentDockSettled();
}

/**
 * Report that the dock's presence is decided, for screenshot capture. The row reserved for the
 * dock's bar is what positions the floating diff nav, so a capture taken before the answer lands
 * freezes that pill a row-height out of place.
 */
function markAgentDockSettled() {
    document.documentElement.dataset.dockSettled = '';
}

/**
 * Mark the layout while the agent is anchored to the side, so the row reserved for the dock's bar
 * collapses: the bar is in the rail with the conversation, and nothing is left at the bottom of the
 * content column to keep space for.
 */
function markAgentDockAnchored(anchored: boolean) {
    document.documentElement.classList.toggle('agent-dock-anchored', anchored);
}

/**
 * Remember where the dock renders, for the splash screen: it reserves the dock's row
 * synchronously on the next load, ahead of the asynchronous auth check that only lands after
 * first paint — and it has no other way to know the organization's AI toggle.
 */
function rememberAgentDockVisible(visible: boolean) {
    try {
        localStorage.setItem('agentDockVisible', JSON.stringify(visible));
    } catch {
        // Storage can be unavailable; the splash then falls back to its auth check alone.
    }
}

function AgentDockForOrganization(props: { organizationId: string; isFullScreen: boolean }) {
    const { organizationId, isFullScreen } = props;
    const hasAI = useAgentEnabled();
    const location = useAgentLocation();

    React.useEffect(() => {
        rememberAgentDockVisible(hasAI);
        // The full-screen agent decides for itself, from whether a background transcript is open.
        if (!isFullScreen) {
            markAgentDockAbsent(!hasAI);
        }
        // Left as it is on unmount: leaving an organization does not say anything about the next
        // one, and the answer is set again as soon as it is known.
    }, [hasAI, isFullScreen]);

    if (!hasAI) {
        return null;
    }

    // The dock offers the upgrade that lifts the free message limit, and the hook behind that offer
    // requires this provider. The dock is mounted in the application frame, above the screens that
    // bring their own, so it carries one itself.
    //
    // The boundary is the dock's own: it fetches conversation lists, and those queries throw during
    // render when the API refuses them. Being mounted in the application frame, above every screen,
    // that throw would otherwise reach the app's boundary and replace the whole product — so a
    // change request that no longer exists would cost the user everything rather than the dock.
    return (
        <AgentDockErrorBoundary
            // Where the user is, which is what the dock's queries are made of: a failure belongs to
            // the place it happened, and moving on is the signal to try again.
            recoverOn={[
                organizationId,
                location.siteId,
                location.spaceId,
                location.changeRequest,
            ].join('/')}
        >
            <UpgradeDialogProvider>
                <AgentDockConversations
                    organizationId={organizationId}
                    isFullScreen={isFullScreen}
                />
            </UpgradeDialogProvider>
        </AgentDockErrorBoundary>
    );
}

/**
 * Render nothing rather than take the application down with the dock.
 *
 * Reported, so a failure here is still visible to us, but not surfaced: the dock is a way in to the
 * agent, and the product remains usable without it.
 */
function AgentDockErrorBoundary(props: { children: React.ReactNode; recoverOn: string }) {
    return (
        <Sentry.ErrorBoundary
            // oxlint-disable-next-line react/no-unstable-nested-components -- Sentry's documented fallback render prop, which is how `resetError` is reached at all.
            fallback={({ resetError }) => (
                <AgentDockRecovery recoverOn={props.recoverOn} onRecover={resetError} />
            )}
            onError={(error) => logger.error('The agent dock failed to render', error)}
        >
            {props.children}
        </Sentry.ErrorBoundary>
    );
}

/**
 * Renders nothing, and gives the dock another go once `recoverOn` changes.
 *
 * Without this a single failure — a change request deleted while it was open, say — costs the user
 * the agent for the rest of the session, long after the request that failed stopped being relevant.
 * Mounted only while the boundary is tripped, so it never remounts a working dock; and the value it
 * was mounted with is deliberately skipped, since retrying the render that just failed would only
 * fail again.
 */
function AgentDockRecovery(props: { recoverOn: string; onRecover: () => void }) {
    const { recoverOn } = props;
    const failedOn = React.useRef(recoverOn);
    const recover = useEventCallback(props.onRecover);
    // While this is mounted there is no dock, and the layout should keep no column for one.
    useMarkAgentDockFailed();

    React.useEffect(() => {
        if (recoverOn !== failedOn.current) {
            recover();
        }
    }, [recoverOn, recover]);

    return null;
}

function AgentDockConversations(props: { organizationId: string; isFullScreen: boolean }) {
    const { organizationId, isFullScreen } = props;
    const {
        tabs,
        activeTabId,
        draftConversationId,
        isDraftActive,
        minimized,
        openTab,
        closeTab,
        closeChangeRequestTabs,
        selectTab,
        openConversation,
        setMinimized,
        toggleDraft,
        startDraft,
        promoteDraft,
        closeDraft,
    } = useAgentDock(organizationId);
    const location = useAgentLocation();
    const { conversations, refetch } = useOrganizationConversations(organizationId, {
        siteId: location.siteId,
    });
    const deleteConversationById = useDeleteOrganizationConversation(organizationId);
    const changeRequest = useCurrentChangeRequest();
    const barSlot = useAgentDockSlot('bar');
    const railSlot = useAgentDockSlot('rail');
    const { mode, canAnchor, setMode } = useAgentDockLayout();
    // Anchored only once the rail is really there. A window too narrow for it renders none, and the
    // agent goes back to the bar without the preference being rewritten — so widening the window
    // puts it back where the user asked for it.
    const anchored = mode === 'anchored' && railSlot !== null;

    // Fetched here rather than inside the history menu: the menu's children are a react-aria
    // collection, so a section cannot be a component that fetches what it lists.
    const { conversations: changeRequestConversations } = useChangeRequestConversations(
        {
            spaceId: changeRequest?.spaceId ?? '',
            // The number: the API resolves either, and it is there a request earlier than the id.
            changeRequestId: changeRequest?.number ?? '',
        },
        { skip: !changeRequest }
    );

    // A change request conversation only makes sense next to the change request it belongs to: its
    // title and its context resolve against the one in front of the user, so a tab left open after
    // navigating away would sit there unnamed. Close it instead — the conversation itself lives in
    // its durable object, so reopening it from the change request loses nothing.
    //
    // Closes the change request this window just *left*, rather than everything that is not the one
    // in front of it. Two reasons: the dock's state is shared between windows, so the second reading
    // would prune what the first just opened; and a tab is opened just before the navigation that
    // makes it current — creating a change request with the agent does exactly that — so it would
    // otherwise close the tab it had only just been given.
    const lastChangeRequestIdRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        const currentId = changeRequest?.id ?? null;
        if (changeRequest && !currentId) {
            // The id is still resolving; this window has not arrived anywhere yet.
            return;
        }

        const left = lastChangeRequestIdRef.current;
        lastChangeRequestIdRef.current = currentId;
        if (left && left !== currentId) {
            closeChangeRequestTabs(left);
        }
    }, [changeRequest, closeChangeRequestTabs]);

    const onPressAgent = useEventCallback(() => {
        // Already have a draft: just toggle it, and do not provision again.
        if (draftConversationId) {
            toggleDraft(draftConversationId);
            return;
        }

        // Opens on an id we make up, so the window appears with no round-trip. Provisioning is
        // started here rather than inside the state updater — an updater can run during a render
        // React then discards, which aborts the request and leaves the agent with no token.
        const conversationId = generateConversationLocalId();
        void provisionOrganizationConversation({ organizationId, conversationId });
        toggleDraft(conversationId);
    });

    const deleteConversation = useEventCallback(async (conversationId: string) => {
        closeTab(getAgentDockTabId({ kind: 'organization', conversationId }));
        await deleteConversationById(conversationId);
        void refetch();
    });

    const tabDescriptors = React.useMemo(
        () =>
            tabs.map((tab) => ({
                tab,
                title: getTabTitle(tab, conversations, changeRequestConversations),
            })),
        [tabs, conversations, changeRequestConversations]
    );

    React.useEffect(() => {
        markAgentDockAnchored(anchored);
        // Cleared on the way out, which is also how an organization with AI switched off gives the
        // row back: the dock stops rendering entirely rather than reporting for itself.
        return () => markAgentDockAnchored(false);
    }, [anchored]);

    /**
     * Open a conversation without the toggle {@link onPressAgent} does — for the panel, which has
     * nothing to put away and so must always have something to show.
     */
    const ensureConversation = useEventCallback(() => {
        if (draftConversationId) {
            startDraft(draftConversationId);
            return;
        }

        const conversationId = generateConversationLocalId();
        void provisionOrganizationConversation({ organizationId, conversationId });
        startDraft(conversationId);
    });

    const setPlacement = useEventCallback((next: AgentDockMode) => {
        setMode(next);
        // Anchoring while the window is minimized would otherwise leave the state saying the
        // conversation is put away, and un-anchoring later would drop it into a closed dock.
        setMinimized(false);
        analytics.logEvent(
            analytics.createOrgEvent(organizationId, {
                type: 'agent_switch_ui_mode',
                // The dock belongs to the organization agent, whichever conversation is open in it.
                agentType: 'organization',
                mode: next === 'anchored' ? 'sidepanel' : 'dock',
            })
        );
    });

    const surface = getAgentDockSurface({
        tabs,
        activeTabId,
        draftConversationId,
        minimized,
        mode: anchored ? 'anchored' : 'dock',
    });

    // The full-screen agent already shows the conversation, so the dock only appears there to show
    // a background agent's transcript, which that screen cannot.
    const hiddenOnFullScreen =
        isFullScreen && !(surface.kind === 'tab' && surface.tab.kind === 'organization_background');
    React.useEffect(() => {
        if (isFullScreen) {
            markAgentDockAbsent(hiddenOnFullScreen);
        }
    }, [isFullScreen, hiddenOnFullScreen]);

    // Nothing to show, anchored: a fresh state, the last tab closed, or a change request left
    // behind taking its conversations with it. The panel opens one rather than sitting empty.
    const isPanelEmpty = anchored && surface.kind === 'none';
    React.useEffect(() => {
        if (isPanelEmpty) {
            ensureConversation();
        }
    }, [isPanelEmpty, ensureConversation]);

    const conversation = (():
        | ({ tab: AgentDockTab; title: string } & AgentDockConversationControls)
        | null => {
        switch (surface.kind) {
            case 'none':
                return null;
            case 'draft':
                return {
                    tab: { kind: 'organization', conversationId: surface.conversationId },
                    title: 'New chat',
                    onStarted: promoteDraft,
                    // Closing belongs to the dock, where the window goes away and leaves its tab
                    // row behind. The panel has nowhere to put anything away to, so closing there
                    // would only start another conversation in its place.
                    ...(anchored ? {} : { onClose: closeDraft }),
                };
            case 'tab': {
                const { tab } = surface;
                return {
                    tab,
                    title: getTabTitle(tab, conversations, changeRequestConversations),
                    ...(anchored ? {} : { onClose: () => closeTab(getAgentDockTabId(tab)) }),
                    // Only organization conversations are the user's to delete; the ones the
                    // change-request agent produced belong to that change request.
                    ...(tab.kind === 'organization'
                        ? { onDelete: () => deleteConversation(tab.conversationId) }
                        : {}),
                };
            }
        }
    })();

    // What the conversation's title offers, minus the way in — that one differs by surface, so each
    // adds its own. Only once the change request's id is resolved: a tab opened on the number
    // addresses a durable object that holds nothing, so there is no conversation to show.
    const historyLists = {
        conversations,
        changeRequestConversations,
        changeRequest: changeRequest?.id
            ? { spaceId: changeRequest.spaceId, changeRequestId: changeRequest.id }
            : null,
    };

    if (hiddenOnFullScreen) {
        return null;
    }

    if (anchored && railSlot) {
        return (
            <AgentSidePanel
                slot={railSlot}
                location={location}
                conversation={conversation}
                // Not the dock's openTab, which adds to a row of open conversations the panel does
                // not have.
                history={{ ...historyLists, onOpenConversation: openConversation }}
                // Not the dock's Agent button, which puts the window away when its conversation is
                // already in front: the panel has nowhere to put anything away to.
                onNewConversation={ensureConversation}
                onUnanchor={() => setPlacement('dock')}
            />
        );
    }

    // The layout owns where the dock appears; until it has mounted its slot (login, onboarding and
    // the other screens rendered without the app layout) there is nowhere to put it.
    if (!barSlot) {
        return null;
    }

    return (
        <AgentDockBar
            slot={barSlot}
            organizationId={organizationId}
            location={location}
            tabs={tabDescriptors}
            activeTabId={activeTabId}
            isDraftActive={isDraftActive}
            minimized={minimized}
            conversation={
                conversation
                    ? {
                          ...conversation,
                          // Offered from the window alone, and only where the rail can exist at
                          // all: on a narrow window there is nowhere to anchor to.
                          ...(canAnchor ? { onAnchor: () => setPlacement('anchored') } : {}),
                      }
                    : null
            }
            history={{ ...historyLists, onOpenConversation: openTab }}
            onPressAgent={onPressAgent}
            onSelectTab={selectTab}
            onCloseTab={closeTab}
            onMinimize={() => setMinimized(true)}
        />
    );
}

/**
 * Title shown on a tab, resolved from the list the conversation came from. Falls back to a
 * placeholder until the agent has generated a title from the first exchange — or, for a change
 * request's conversation, whenever the user has navigated away from the change request that lists it.
 */
function getTabTitle(
    tab: AgentDockTab,
    conversations: api.AgentConversation[],
    changeRequestConversations: api.AgentConversation[]
): string {
    // A background agent has no conversation row of its own: it carries the few words the agent
    // that started it named the work with.
    if (tab.kind === 'organization_background') {
        return tab.title.trim() || 'Background agent';
    }

    const [list, fallback] =
        tab.kind === 'change_request'
            ? ([changeRequestConversations, 'Change request'] as const)
            : ([conversations, 'New chat'] as const);
    const conversation = list.find((candidate) => candidate.id === tab.conversationId);
    return conversation?.title?.trim() || fallback;
}

/**
 * The screen whose content the user is looking at.
 *
 * A space screen can be opened over another one: selecting a change request in the change requests
 * list opens it as an overlay, and `screen` goes on describing the list underneath. The dock follows
 * the overlay, or the context would be the list the user left behind rather than the change request
 * in front of them. Every other kind of overlay is a dialog — an invite, a settings sheet — laid
 * over content the user is still in, which is why only a space overlay takes precedence.
 */
function useForegroundScreen(): NavigationScreenState {
    const { screen, overlay } = useNavigationState();
    return overlay?.type === 'space' ? overlay : screen;
}

/**
 * The change request the user is currently looking at, which the agent attaches as context and
 * whose own agent conversations become reachable from the history.
 *
 * Carries both of its identifiers, because they are not interchangeable. The route — and so the
 * API, which resolves either — carries the number, while the change-request agent's durable object
 * is addressed by the id: connecting on the number reaches a different object entirely, one that
 * holds no conversation. The id costs a request, so it arrives after the number.
 */
function useCurrentChangeRequest(): {
    spaceId: string;
    number: string;
    id: string | null;
} | null {
    const screen = useForegroundScreen();

    const route = React.useMemo(() => {
        if (screen.type !== 'space' || !screen.changeRequest) {
            return null;
        }
        return { spaceId: screen.space, number: String(screen.changeRequest) };
    }, [screen]);

    const { data: changeRequest } = useQuery(
        apiQuery(
            'getChangeRequestById',
            route ? { spaceId: route.spaceId, changeRequestId: route.number } : skipToken
        )
    );

    return React.useMemo(
        () => (route ? { ...route, id: changeRequest?.id ?? null } : null),
        [route, changeRequest?.id]
    );
}

/**
 * Where the user is, as the agent needs to hear it.
 *
 * The agent is scoped to an organization, so without this it has no way to resolve "this site" and
 * has to go looking. Memoised on its own values rather than on the screen object so that navigating
 * within the same site does not hand the connection a new — but equal — location.
 */
function useAgentLocation(): OrganizationAgentLocation {
    const screen = useForegroundScreen();
    const site = 'site' in screen ? screen.site : undefined;
    const space = screen.type === 'space' ? screen.space : undefined;
    // Anything falsy is a route with no change request in it: a number starts at 1, an id is never
    // empty. Normalized here so everything downstream can ask whether there is one at all.
    const changeRequest =
        screen.type === 'space' && screen.changeRequest ? screen.changeRequest : undefined;
    // Read rather than derived from the screen: this is the address the user is actually looking at,
    // and it carries what the ids leave out — the page, the mode, the tab. Safe to read during
    // render because navigating is what re-renders this, so it is never behind the screen state.
    const url = typeof window === 'undefined' ? undefined : getAgentLocationUrl(window.location);

    return React.useMemo(
        () => ({
            ...(url ? { url } : {}),
            ...(site ? { siteId: site } : {}),
            ...(space ? { spaceId: space } : {}),
            ...(changeRequest !== undefined ? { changeRequest } : {}),
        }),
        [url, site, space, changeRequest]
    );
}

/** The path, plus the analytics `filters` when there are some: other parameters can carry secrets.
 * Decoded, so the agent reads the rison rather than its percent-encoding. */
function getAgentLocationUrl(location: Location): string {
    const filters = new URLSearchParams(location.search).get('filters');
    return filters ? `${location.pathname}?filters=${filters}` : location.pathname;
}
